import * as Portal from "@radix-ui/react-portal";
import * as React from "react";
import useSound from "use-sound";
import { Icon, type IconName } from "~/components/icon";
import { cn, generateId } from "~/lib/utils";
import pop from "../../sounds/pop.wav";

const MAX_TOASTS = 5;

export interface ToastsContextValue {
  toasts: ToastProps[];
  addToast: (options: ToastProps) => void;
  removeToast: (id: string) => void;
}

export interface ToastAction {
  label: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

export interface ToastProps {
  content: React.ReactNode | string;
  variant?: "default" | "info" | "success" | "error" | "warning";
  stacked?: boolean;
  icon?: IconName;
  id?: string;
  autoRemove?: boolean;
  action?: ToastAction | React.ReactNode;
  cancel?: ToastAction | React.ReactNode;
  dismissible?: boolean;
}

export interface ToastsProviderProps {
  children: React.ReactNode;
}

const isValidAction = (
  action: ToastAction | React.ReactNode,
): action is ToastAction => {
  return (action as ToastAction)?.label !== undefined;
};

const ToastsContext = React.createContext<ToastsContextValue>({
  toasts: [],
  addToast: () => {},
  removeToast: () => {},
});

export const useToasts = () => {
  const toastsContextValue = React.useContext(ToastsContext);

  if (!toastsContextValue) {
    throw new Error("`useToasts` must be wrapped in an <ToastsProvider />");
  }
  return toastsContextValue;
};

export const ToastsProvider = ({ children }: ToastsProviderProps) => {
  const [toasts, setToasts] = React.useState<ToastProps[]>([]);
  const [play] = useSound(pop);

  const addToast = (props: ToastProps) => {
    const {
      content,
      variant = "default",
      icon,
      stacked,
      id = generateId(),
      autoRemove = true,
      action,
      cancel,
      dismissible = true,
    } = props;
    setToasts((prevToasts) =>
      [
        ...prevToasts,
        {
          content,
          variant,
          stacked,
          icon,
          id,
          autoRemove,
          action,
          cancel,
          dismissible,
        },
      ].slice(-MAX_TOASTS),
    );
    // play();
  };

  const removeToast = (id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  return (
    <ToastsContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <Portal.Root>
        <Toaster />
      </Portal.Root>
    </ToastsContext.Provider>
  );
};

export const Toast = (props: ToastProps) => {
  const {
    content,
    variant = "default",
    icon: customIcon,
    stacked,
    id = generateId(),
    autoRemove = false,
    action,
    cancel,
    dismissible = true,
  } = props;
  const [active, setActive] = React.useState<boolean>(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const { removeToast } = useToasts();

  const stopTimer = React.useCallback(() => {
    if (!timeoutRef.current) return;
    clearTimeout(timeoutRef.current);
  }, []);

  const startTimer = React.useCallback(() => {
    if (!autoRemove && !dismissible) return;

    stopTimer();
    timeoutRef.current = setTimeout(() => {
      removeToast(id);
    }, 5000);
  }, [stopTimer, id]);

  React.useEffect(() => {
    if (active) stopTimer();
    else startTimer();
  }, [active, startTimer, stopTimer]);

  const icon = customIcon
    ? customIcon
    : variant === "info"
      ? "info-circle"
      : variant === "success"
        ? "check-circle"
        : variant === "warning"
          ? "alert-triangle"
          : variant === "error"
            ? "alert-circle"
            : null;

  return (
    <div
      className={cn("fui-toast", {
        "fui-toast--info": variant === "info",
        "fui-toast--success": variant === "success",
        "fui-toast--warning": variant === "warning",
        "fui-toast--error": variant === "error",
        "fui-toast--stacked": stacked,
      })}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
    >
      {icon && (
        <div className={cn("fui-toast__icon")}>
          <Icon name={icon} className={cn("w-6 h-6")} />
        </div>
      )}
      <div className={cn("fui-toast__wrapper")}>
        <div className={cn("fui-toast__content")}>
          <div className={cn("text-sm")}>{content}</div>
        </div>
        {(action || cancel) && (
          <div className={cn("fui-toast__actions")}>
            {React.isValidElement(action) ? (
              action
            ) : action && isValidAction(action) ? (
              <button
                className={cn(
                  "fui-button fui-button--primary fui-button--small",
                  { "fui-button--danger": variant === "error" },
                )}
                onClick={(event) => {
                  action.onClick?.(event);
                  removeToast(id);
                }}
              >
                {action.label}
              </button>
            ) : null}
            {React.isValidElement(cancel) ? (
              cancel
            ) : cancel && isValidAction(cancel) ? (
              <button
                className={cn(
                  "fui-button fui-button--outline fui-button--small",
                  { "fui-button--danger": variant === "error" },
                )}
                onClick={(event) => {
                  cancel.onClick?.(event);
                  removeToast(id);
                }}
              >
                {cancel.label}
              </button>
            ) : null}
          </div>
        )}
      </div>
      {dismissible && (
        <button
          className={cn("fui-toast__close")}
          onClick={() => removeToast(id)}
        >
          <Icon name="x-close" className={cn("w-4 h-4")} />
          <span className="sr-only">Close</span>
        </button>
      )}
    </div>
  );
};

const Toaster = () => {
  const { toasts } = useToasts();

  return (
    <>
      {toasts.length > 0 && (
        <div className={cn("fui-toaster")}>
          {toasts.map((toast, i) => {
            return <Toast key={i} {...toast} />;
          })}
        </div>
      )}
    </>
  );
};
